import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormikContext } from 'formik'
import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import * as Api from 'src/api'
import { CourseCompletedIcon } from 'src/assets/icons/customIcons/course-icons/CourseCompleted'
import { CourseFailedIcon } from 'src/assets/icons/customIcons/course-icons/CourseFailed'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface FormikValues {
  readonly query: string
  readonly status: string
}
export default function AdministratorMailsPage() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('common:mails')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent() {
  const t = useTranslatable()
  const locale = useLocale()
  const breadcrumbsItems = [{ page: `${t('common:mails')}`, path: `/${locale}/administrator/mails` }]
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    perPage: '10',
    query: '',
    status: '',
  })
  const query = searchParams.get('query') || ''
  const status = searchParams.get('status') || ''
  const initialValues: FormikValues = { query, status }

  return (
    <Section
      title={t('common:mails')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faEnvelope} />}
    >
      <Form
        initialValues={initialValues}
        onSubmit={async (values: FormikValues) => {
          setSearchParams((params) => {
            Object.entries(values).forEach(([key, value]) => {
              params.set(key, value)
            })

            return params
          })
        }}
        classNames={{
          form: 'w-full px-2 mt-3 mb-1',
        }}
        enableReinitialize
      >
        <FormSearch />
      </Form>

      <SuspenseWrapper>
        <TableContent searchParams={searchParams} />
      </SuspenseWrapper>
    </Section>
  )
}

function TableContent({ searchParams }: { searchParams: URLSearchParams }): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const query = searchParams.get('query') || ''
  const status = searchParams.get('status') || ''
  const page = searchParams.get('page')!
  const perPage = searchParams.get('perPage')!
  const canManageMails = useAdminAccess().canManage('mails')

  const { data: mails, mutate: fetchMails } = useApi({
    endpoint: Api.getAdministratorMailsList,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          filters: {
            search: query!,
            status: status!,
          },
          page: parseInt(page, 10),
          perPage: parseInt(perPage, 10),
        },
      }),
      [headers, perPage, query, status, page]
    ),
  })

  if (!mails || !mails.data || mails.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }
  return (
    <div className="mb-7">
      <PaginationTop pagination={mails.meta.pagination} />
      <Table.Table className="mt-7">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="!pl-6">{t('common:unique')}</Table.Th>
            <Table.Th>{t('person:address')}</Table.Th>
            <Table.Th>{t('common:title')}</Table.Th>
            <Table.Th>{t('common:status')}</Table.Th>
            <Table.Th>{t('common:date')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {mails.data.map((mail) => (
            <tr key={mail.id}>
              <Table.Td className="whitespace-nowrap">{mail.id}</Table.Td>
              <Table.Td>{mail.address}</Table.Td>
              <Table.Td>{mail.subject}</Table.Td>
              <Table.Td>
                <MailStatus mail={mail} fetchMails={fetchMails} canManageMails={canManageMails} />
              </Table.Td>
              <Table.Td className="whitespace-nowrap">{mail.date}</Table.Td>
            </tr>
          ))}
        </Table.Tbody>
      </Table.Table>
      <PaginationBottom pagination={mails.meta.pagination} />
    </div>
  )
}

function MailStatus({
  mail,
  fetchMails,
  canManageMails,
}: {
  mail: Api.Mail
  fetchMails: () => void
  canManageMails: boolean
}): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const theme = useTheme()
  const [sendingMail, setSendingMail] = useState(false)
  const resendMail = React.useCallback(async (): Promise<void> => {
    if (!canManageMails) {
      return
    }
    try {
      setSendingMail(true)
      await Api.postAdministratorMailsTryAgain({
        headers,
        body: {
          id: mail.id!,
        },
      })
      toast.success(t('uncategorized:message_successfully_sent'))
    } catch (error) {
      toast.error(t('error:an_error_occurred'))
    } finally {
      setSendingMail(false)
      fetchMails()
    }
  }, [fetchMails, headers, mail.id, t, canManageMails])
  return (
    <div className="flex items-center justify-center" data-tooltip-id={mail.id!}>
      <Tooltip id={mail.id!} place="top" variant={theme === 'dark' ? 'dark' : 'light'}>
        {mail.statusMsg ?? mail.status}
      </Tooltip>
      {mail.status === 'FAILED' ? (
        <button
          className="flex items-center text-sm text-primaryTextColor"
          disabled={!canManageMails}
          onClick={resendMail}
          type="button"
        >
          {sendingMail ? <Loader width="30px" /> : <CourseFailedIcon />}
          <span className="ml-2">{t('common:resend')}</span>
        </button>
      ) : mail.status === 'PROCESSING' ? (
        <Loader width="30px" />
      ) : (
        <CourseCompletedIcon />
      )}
    </div>
  )
}

function FormSearch(): React.ReactElement | null {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const [searchParams, setSearchParams] = useSearchParams()
  const status = searchParams.get('status') || ''
  const query = searchParams.get('query') || ''
  const { data: mailsStatuses, isValidating: isMailsStatusesValidating } = useApi({
    endpoint: Api.getAdministratorMailsStatuses,
    params: React.useMemo(
      () => ({
        headers,
        suspense: false,
      }),
      [headers]
    ),
  })

  const mailStatusOptions = React.useMemo(() => {
    if (!mailsStatuses) return []
    return mailsStatuses.data.map((status) => ({
      value: status.id || '',
      label: status.label,
    }))
  }, [mailsStatuses])
  const isDirty = formik.values.query !== query || formik.values.status !== status
  const canCancel = query === '' && status === ''
  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col justify-between">
      <FormError />
      <div className="mb-3 flex w-full flex-wrap justify-between">
        <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
          <TextInput
            type="text"
            label={t('common:search_for_keywords')}
            name="query"
            placeholder={t('common:search')}
          />
        </div>
        <div className="w-full min-w-[100px] md:w-[49%]">
          <SelectInput
            label={t('common:status')}
            name="status"
            options={mailStatusOptions}
            disabled={formik.isSubmitting || isMailsStatusesValidating}
            isLoading={isMailsStatusesValidating}
          />
        </div>
      </div>

      <div className="flex w-full justify-end">
        <Button
          variant="red"
          disabled={formik.isSubmitting || !isDirty}
          type="submit"
          onClick={() => {
            formik.submitForm()
          }}
        >
          {t('common:search')}
        </Button>
        <Button
          variant="yellow"
          onClick={() => {
            setSearchParams({
              query: '',
              status: '',
              page: '1',
              perPage: '10',
            })
            formik.resetForm()
            formik.submitForm()
          }}
          type="reset"
          disabled={formik.isSubmitting || canCancel}
          className="ml-2"
        >
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}
